/**
 * Voice Turn Protocol — `chat.turn.*` WebSocket frame support.
 *
 * Phase 2 of voice turn-taking (#170).
 *
 * Sends `chat.turn.start`, `chat.turn.append`, `chat.turn.update`,
 * `chat.turn.commit`, and `chat.turn.cancel` frames to the gateway.
 *
 * BACKWARDS COMPATIBLE: If the gateway doesn't support `chat.turn.*`
 * (detected via capability negotiation or fire-and-forget semantics),
 * all methods are safe no-ops. The frontend never waits for or depends
 * on a gateway response to any `chat.turn.*` message.
 *
 * Capability detection:
 * - The gateway's hello-ok response may include a `caps` array.
 * - If 'voice-turns' is in caps, we send turn frames.
 * - Otherwise, all send methods are silent no-ops.
 * - Can also be force-enabled via localStorage for testing.
 */

import type { WSClient } from '../ws/client';
import { remoteLog } from '../utils/remoteLog';

// ── Types ──

export type TurnUpdateKind = 'speech_start' | 'speech_end';

export interface TurnStartParams {
  sessionKey: string;
  turnId: string;
}

export interface TurnAppendParams {
  sessionKey: string;
  turnId: string;
  text: string;
  segmentIndex: number;
}

export interface TurnUpdateParams {
  sessionKey: string;
  turnId: string;
  kind: TurnUpdateKind;
  /** Timestamp of the speech event (ms since epoch) */
  ts: number;
}

export interface TurnCommitParams {
  sessionKey: string;
  turnId: string;
  fullText: string;
  segmentCount: number;
  /** How the turn was committed (heuristic path or force) */
  commitReason: string;
}

export interface TurnCancelParams {
  sessionKey: string;
  turnId: string;
  reason: string;
}

// ── localStorage override ──

const LS_FORCE_TURN_PROTOCOL = 'talkyn-force-turn-protocol';

// ── Turn Protocol Manager ──

/**
 * Manages sending `chat.turn.*` protocol frames.
 *
 * All methods are fire-and-forget — they never await a response.
 * If the gateway doesn't support voice turns, all sends are no-ops.
 */
export class TurnProtocolManager {
  private client: WSClient | null = null;
  private sessionKey: string = '';
  private _enabled = false;
  private _turnCounter = 0;
  private turnSessionKeys = new Map<string, string>();

  /**
   * Configure the manager with a WS client and session key.
   * Call after gateway connects and hello-ok is received.
   *
   * @param client - WebSocket client
   * @param sessionKey - Active session key
   * @param gatewayCaps - Capabilities from hello-ok (if available)
   */
  configure(
    client: WSClient | null,
    sessionKey: string,
    gatewayCaps?: string[],
  ): void {
    this.client = client;
    this.sessionKey = sessionKey;
    this.turnSessionKeys.clear();

    // Check for force-enable via localStorage (for testing)
    const forceEnabled = localStorage.getItem(LS_FORCE_TURN_PROTOCOL) === 'true';

    // Check gateway capabilities
    const gatewaySupports = gatewayCaps?.includes('voice-turns') ?? false;

    this._enabled = forceEnabled || gatewaySupports;

    remoteLog('turnProtocol', 'configure', {
      enabled: this._enabled,
      forceEnabled,
      gatewaySupports,
      caps: gatewayCaps?.join(',') ?? 'none',
    });
  }

  /** Whether the protocol is active (gateway supports it or force-enabled) */
  get enabled(): boolean {
    return this._enabled;
  }

  /** Generate a unique turn ID */
  generateTurnId(): string {
    return `turn-${++this._turnCounter}-${Date.now()}`;
  }

  private resolveSessionKey(turnId: string): string {
    return this.turnSessionKeys.get(turnId) ?? this.sessionKey;
  }

  /**
   * Send `chat.turn.start` — a new voice turn has begun.
   * Fire-and-forget: never awaits response.
   */
  sendStart(turnId: string): void {
    if (!this._enabled || !this.client?.isOpen) return;

    const sessionKey = this.sessionKey;
    this.turnSessionKeys.set(turnId, sessionKey);

    const params: TurnStartParams = {
      sessionKey,
      turnId,
    };

    try {
      this.client.sendRaw({
        type: 'req',
        id: `turn-start-${turnId}`,
        method: 'chat.turn.start',
        params,
      });
      remoteLog('turnProtocol', 'sent_start', { turnId, sessionKey });
    } catch (err) {
      remoteLog('turnProtocol', 'send_error', { method: 'start', error: String(err) });
    }
  }

  /**
   * Send `chat.turn.append` — a new transcript segment added to the turn.
   * Fire-and-forget: never awaits response.
   */
  sendAppend(turnId: string, text: string, segmentIndex: number): void {
    if (!this._enabled || !this.client?.isOpen) return;

    const sessionKey = this.resolveSessionKey(turnId);
    const params: TurnAppendParams = {
      sessionKey,
      turnId,
      text,
      segmentIndex,
    };

    try {
      this.client.sendRaw({
        type: 'req',
        id: `turn-append-${turnId}-${segmentIndex}`,
        method: 'chat.turn.append',
        params,
      });
      remoteLog('turnProtocol', 'sent_append', {
        turnId,
        sessionKey,
        segmentIndex,
        text: text.substring(0, 40),
      });
    } catch (err) {
      remoteLog('turnProtocol', 'send_error', { method: 'append', error: String(err) });
    }
  }

  /**
   * Send `chat.turn.update` — speech state changed during a turn.
   * Fire-and-forget: never awaits response.
   *
   * @param ts - Optional event timestamp (ms since epoch). Defaults to `Date.now()`.
   */
  sendUpdate(turnId: string, kind: TurnUpdateKind, ts: number = Date.now()): void {
    if (!this._enabled || !this.client?.isOpen) return;

    const sessionKey = this.resolveSessionKey(turnId);
    const params: TurnUpdateParams = {
      sessionKey,
      turnId,
      kind,
      ts,
    };

    try {
      this.client.sendRaw({
        type: 'req',
        id: `turn-update-${turnId}-${kind}-${ts}`,
        method: 'chat.turn.update',
        params,
      });
      remoteLog('turnProtocol', 'sent_update', { turnId, sessionKey, kind, ts });
    } catch (err) {
      remoteLog('turnProtocol', 'send_error', { method: 'update', error: String(err) });
    }
  }

  /**
   * Send `chat.turn.commit` — the turn is complete, process it.
   * Fire-and-forget: never awaits response.
   */
  sendCommit(
    turnId: string,
    fullText: string,
    segmentCount: number,
    commitReason: string,
  ): void {
    if (!this._enabled || !this.client?.isOpen) return;

    const sessionKey = this.resolveSessionKey(turnId);
    const params: TurnCommitParams = {
      sessionKey,
      turnId,
      fullText,
      segmentCount,
      commitReason,
    };

    try {
      this.client.sendRaw({
        type: 'req',
        id: `turn-commit-${turnId}`,
        method: 'chat.turn.commit',
        params,
      });
      remoteLog('turnProtocol', 'sent_commit', {
        turnId,
        sessionKey,
        segmentCount,
        commitReason,
        text: fullText.substring(0, 60),
      });
    } catch (err) {
      remoteLog('turnProtocol', 'send_error', { method: 'commit', error: String(err) });
    } finally {
      this.turnSessionKeys.delete(turnId);
    }
  }

  /**
   * Send `chat.turn.cancel` — the turn was abandoned.
   * Fire-and-forget: never awaits response.
   */
  sendCancel(turnId: string, reason: string): void {
    if (!this._enabled || !this.client?.isOpen) return;

    const sessionKey = this.resolveSessionKey(turnId);
    const params: TurnCancelParams = {
      sessionKey,
      turnId,
      reason,
    };

    try {
      this.client.sendRaw({
        type: 'req',
        id: `turn-cancel-${turnId}`,
        method: 'chat.turn.cancel',
        params,
      });
      remoteLog('turnProtocol', 'sent_cancel', { turnId, sessionKey, reason });
    } catch (err) {
      remoteLog('turnProtocol', 'send_error', { method: 'cancel', error: String(err) });
    } finally {
      this.turnSessionKeys.delete(turnId);
    }
  }

  /**
   * Update the session key (e.g., on session switch).
   */
  setSessionKey(sessionKey: string): void {
    this.sessionKey = sessionKey;
  }

  /**
   * Tear down — clear refs.
   */
  destroy(): void {
    this.client = null;
    this._enabled = false;
    this.turnSessionKeys.clear();
  }
}
